import { BrandLoader } from "@/components/brand/BrandLoader";
import { Logo } from "@/components/brand/Logo";
import { ChatWindow } from "@/components/chat/ChatWindow";
import { ContractGate } from "@/components/chat/ContractGate";
import { LanguageGate } from "@/components/chat/LanguageGate";
import { LanguageSwitcher } from "@/components/chat/LanguageSwitcher";
import { QuickReplyChips } from "@/components/chat/QuickReplyChips";
import { chatService } from "@/lib/api";
import { readContractSkip, writeContractSkip } from "@/lib/contract";
import { formatDate } from "@/lib/format";
import { useGuestLanguage } from "@/stores/language";
import type { ActionButton, ChatMessage } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarCheck, DoorOpen, FileText, MapPinOff } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

export function GuestChatPage() {
  const { token = "" } = useParams<{ token: string }>();
  const { lang, setLang } = useGuestLanguage(token);
  const queryClient = useQueryClient();
  const sessionKey = ["guest-session", token, lang];
  const [optimistic, setOptimistic] = useState<ChatMessage[]>([]);
  const [contractSkipped, setContractSkipped] = useState(() => readContractSkip(token));
  const [viewingContract, setViewingContract] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: sessionKey,
    queryFn: () => chatService.session(token, lang ?? undefined),
    enabled: Boolean(token) && Boolean(lang),
    retry: false,
  });

  const sendMutation = useMutation({
    mutationFn: (vars: { message: string; guideId?: string }) =>
      chatService.send(token, vars.message, { lang: lang ?? undefined, guideId: vars.guideId }),
    onSuccess: () => {
      setOptimistic([]);
      queryClient.invalidateQueries({ queryKey: sessionKey });
    },
    onError: () => {
      setOptimistic((prev) => prev.filter((m) => m.role !== "user"));
      toast.error("메시지를 보내지 못했습니다. 다시 시도해주세요.");
    },
  });

  const dispatch = (content: string, guideId?: string) => {
    setOptimistic((prev) => [
      ...prev,
      {
        id: `optimistic-${Date.now()}`,
        guest_id: "",
        role: "user",
        content,
        created_at: new Date().toISOString(),
      },
    ]);
    sendMutation.mutate({ message: content, guideId });
  };

  const handleSend = (content: string) => dispatch(content);
  const handlePick = (button: ActionButton) => dispatch(button.label, button.guide_id);

  const contractPending = data?.contract_status === "pending" && !contractSkipped;
  const contractAccepted = data?.contract_status === "accepted";

  const contractQuery = useQuery({
    queryKey: ["guest-contract", token, lang],
    queryFn: () => chatService.contract(token, lang ?? undefined),
    // 체결 후에도 읽는다 — 게스트가 자기가 동의한 내용을 다시 열 수 있어야 한다
    // (전자문서법 제5조 열람 보장). 체결 뒤 본문은 계약서 원본이 아니라 스냅샷이다.
    enabled: Boolean(token) && Boolean(lang) && (contractPending || contractAccepted),
    retry: false,
  });

  const acceptMutation = useMutation({
    mutationFn: () => chatService.acceptContract(token, lang ?? undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sessionKey });
      // 체결 전 응답(pending)이 캐시에 남으면 "계약서" 버튼이 스냅샷을 못 연다
      queryClient.invalidateQueries({ queryKey: ["guest-contract", token] });
    },
    onError: () => {
      toast.error("계약 체결에 실패했습니다. 다시 시도해주세요.");
      // 409(계약서 소멸) 포함 — 세션을 다시 읽어 게이트 상태를 재판단
      queryClient.invalidateQueries({ queryKey: sessionKey });
    },
  });

  if (!lang) {
    return <LanguageGate onSelect={setLang} />;
  }

  if (isLoading) {
    return (
      <div className="bg-atmosphere grain">
        <BrandLoader fullscreen label="컨시어지를 부르는 중…" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="bg-atmosphere grain flex h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
        <div className="relative z-10 flex flex-col items-center gap-4">
          <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
            <MapPinOff className="h-7 w-7" />
          </span>
          <div className="space-y-1.5">
            <h1 className="font-display text-2xl tracking-tight text-foreground">
              유효하지 않은 링크입니다
            </h1>
            <p className="max-w-xs text-sm text-muted-foreground">
              링크가 만료되었거나 잘못되었습니다. 숙소에 문의해주세요.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // 조회 실패 시 게스트를 막지 않고 챗으로 진행 — 세션은 pending으로 남아 다음 방문에서 재시도
  if (contractPending && !contractQuery.isError) {
    if (contractQuery.isLoading || !contractQuery.data) {
      return (
        <div className="bg-atmosphere grain">
          <BrandLoader fullscreen label="계약서를 불러오는 중…" />
        </div>
      );
    }
    if (contractQuery.data.status === "pending") {
      return (
        <ContractGate
          propertyName={data.property.name}
          contract={contractQuery.data}
          accepting={acceptMutation.isPending}
          onAccept={() => acceptMutation.mutate()}
          onSkip={
            contractQuery.data.required
              ? undefined
              : () => {
                  writeContractSkip(token);
                  setContractSkipped(true);
                }
          }
        />
      );
    }
    // status가 none/accepted로 바뀐 경우(그 사이 삭제/체결) — 게이트 없이 챗으로 진행
  }

  // 체결본 열람 — 같은 화면을 읽기 전용으로 다시 연다.
  if (viewingContract && contractQuery.data?.status === "accepted") {
    return (
      <ContractGate
        propertyName={data.property.name}
        contract={contractQuery.data}
        onAccept={() => setViewingContract(false)}
        onClose={() => setViewingContract(false)}
        documentUrl={chatService.contractDocumentUrl(token)}
      />
    );
  }

  const greetingMessage: ChatMessage = {
    id: "greeting",
    guest_id: "",
    role: "assistant",
    content: data.greeting,
    created_at: data.guest.check_in ?? new Date(0).toISOString(),
  };
  const messages: ChatMessage[] = [greetingMessage, ...data.messages, ...optimistic];

  const lead = (
    <section className="animate-rise overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm">
      <div className="relative h-36 sm:h-40">
        {data.intro_image_urls?.length ? (
          <div className="flex h-full gap-2 overflow-x-auto">
            {data.intro_image_urls.map((u) => (
              <img
                key={u}
                src={u}
                alt=""
                className="h-full w-full flex-shrink-0 object-cover object-[50%_60%]"
              />
            ))}
          </div>
        ) : (
          <img
            src="/brand/welcome-scene.png"
            alt=""
            className="h-full w-full object-cover object-[50%_60%]"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-card via-card/55 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-4">
          <p className="eyebrow-brass">환영합니다 · Welcome</p>
          <h1 className="font-display text-2xl leading-tight tracking-tight text-foreground">
            {data.guest.name}님, 반갑습니다
          </h1>
        </div>
      </div>
      {data.intro ? (
        <p className="px-4 pt-3 text-sm leading-relaxed text-muted-foreground">{data.intro}</p>
      ) : null}
      {(data.guest.check_in || data.guest.check_out || data.room) && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 px-4 py-3 text-xs text-muted-foreground">
          {data.room && (
            <span className="inline-flex items-center gap-1.5">
              <DoorOpen className="h-3.5 w-3.5 text-primary" /> {data.room.name}
            </span>
          )}
          {data.guest.check_in && (
            <span className="inline-flex items-center gap-1.5">
              <CalendarCheck className="h-3.5 w-3.5 text-accent" /> 체크인{" "}
              {formatDate(data.guest.check_in)}
            </span>
          )}
          {data.guest.check_out && (
            <span className="inline-flex items-center gap-1.5">
              <CalendarCheck className="h-3.5 w-3.5 text-brass" /> 체크아웃{" "}
              {formatDate(data.guest.check_out)}
            </span>
          )}
        </div>
      )}
    </section>
  );

  return (
    <div className="bg-atmosphere grain flex h-dvh flex-col">
      <header className="sticky top-0 z-20 border-b border-border/50 bg-card/70 backdrop-blur-md">
        <div className="mx-auto flex w-full max-w-2xl items-center justify-between gap-3 px-4 py-2.5">
          <div className="flex min-w-0 items-center gap-2.5">
            <Logo size={32} />
            <p className="truncate font-display text-base font-medium tracking-tight text-foreground">
              {data.property.name}
            </p>
          </div>
          <div className="flex items-center gap-1">
            {contractAccepted ? (
              <button
                type="button"
                onClick={() => setViewingContract(true)}
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
              >
                <FileText className="h-3.5 w-3.5" />
                계약서
              </button>
            ) : null}
            <LanguageSwitcher value={lang} onChange={setLang} />
          </div>
        </div>
      </header>
      <ChatWindow
        messages={messages}
        isTyping={sendMutation.isPending}
        disabled={sendMutation.isPending}
        onSend={handleSend}
        lead={lead}
        belowMessages={
          <QuickReplyChips
            buttons={data.action_buttons}
            disabled={sendMutation.isPending}
            onPick={handlePick}
          />
        }
      />
    </div>
  );
}
